import { Box, Typography } from '@mui/material';
import { SimpleForm, useNotify, useRefresh, useTranslate } from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { AdvertisementFormContent } from './AdvertisementFormContent.jsx';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { transformAdvertisement } from '@/helpers/marketing/transformAdvertisement.js';
import { validateAdvertisementForm } from '@/helpers/marketing/validateAdvertisement.js';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';

const StyledAdvertisementCreate = styled(Box)(() => ({
    '& .form-content': {
        '& .range-picker-label': {
            backgroundColor: Palette.white,
        },
        '& .has-preview': {
            minWidth: '600px',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                minWidth: '100%',
            },
        },
        '& .MuiFormHelperText-root': {
            display: 'none',
            '&.Mui-error': {
                display: 'block',
            },
        },
    },
}));

export const AdvertisementCreate = ({ handleClose = () => {} }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const queryClient = useQueryClient();

    const validateForm = useCallback(values => validateAdvertisementForm(values, translate), [translate]);

    return (
        <StyledAdvertisementCreate>
            <Box>
                <Box className='popup-wrapper popup-wrapper-top border-bottom'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('resources.adsCampaign.labels.createAdvertisement')}</Typography>
                        <Box>
                            <CustomCloseButton onClose={handleClose} />
                        </Box>
                    </Box>
                </Box>
                <CustomCreate
                    handleClose={handleClose}
                    resource='ad'
                    transform={transformAdvertisement}
                    mutationOptions={{
                        onSuccess: () => {
                            notify(translate('resources.adsCampaign.labels.successAdded'), { type: 'success', multiLine: true });
                            queryClient.invalidateQueries({ queryKey: ['ad'] });
                            refresh();
                            handleClose();
                        },
                    }}
                >
                    <Box className='popup-wrapper'>
                        <SimpleForm
                            mode='onSubmit'
                            reValidateMode='onSubmit'
                            toolbar={
                                <TagCategoryToolbar
                                    onCancel={handleClose}
                                    cancelLabel={translate('common.cancel')}
                                    saveLabel={translate('resources.adsCampaign.labels.publish')}
                                />
                            }
                            validate={validateForm}
                        >
                            <AdvertisementFormContent />
                        </SimpleForm>
                    </Box>
                </CustomCreate>
            </Box>
        </StyledAdvertisementCreate>
    );
};

AdvertisementCreate.propTypes = {
    handleClose: PropTypes.func,
};
